import { Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  usePermissions,
  useHasPermission,
  useHasAnyPermission,
  useHasAllPermissions,
} from 'src/hooks/use-permissions';

// ----------------------------------------------------------------------

/**
 * Componente para proteger elementos de la UI basado en permisos específicos
 * Solo renderiza los children si el usuario tiene los permisos requeridos
 */
const PermissionGuard = ({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  disable = false,
}) => {
  // Determinar qué hook usar basado en los props
  const hasPermission = useHasPermission(permission);
  const hasAnyPermissions = useHasAnyPermission(permissions);
  const hasAllPermissions = useHasAllPermissions(permissions);

  // Lógica para determinar si debe renderizar
  let shouldRender = false;

  if (permission) {
    // Si se especifica un permiso único
    shouldRender = hasPermission;
  } else if (permissions && Array.isArray(permissions)) {
    // Si se especifica un array de permisos
    shouldRender = requireAll ? hasAllPermissions : hasAnyPermissions;
  } else {
    // Si no hay permisos especificados, no renderizar por defecto
    shouldRender = false;
  }

  // Si está deshabilitado, siempre renderizar
  if (disable) {
    shouldRender = true;
  }

  // Renderizar condicionalmente
  if (shouldRender) {
    return <>{children}</>;
  }

  // Renderizar fallback si se proporciona
  if (fallback !== null) {
    return <>{fallback}</>;
  }

  // No renderizar nada
  return null;
};

PermissionGuard.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  fallback: PropTypes.node,
  disable: PropTypes.bool,
};

export default PermissionGuard;

// ----------------------------------------------------------------------

/**
 * HOC (Higher-Order Component) para proteger componentes basado en permisos
 * @param {string|string[]} permissions - Permiso(s) requerido(s)
 * @param {Object} options - Opciones de configuración
 * @returns {Function} - Componente envuelto con protección
 */
export const withPermission =
  (permissions, options = {}) =>
  (WrappedComponent) => {
    const { requireAll = false, fallback = null } = options;

    const ProtectedComponent = (props) => {
      const guardProps = Array.isArray(permissions)
        ? { permissions, requireAll }
        : { permission: permissions };

      return (
        <PermissionGuard {...guardProps} fallback={fallback}>
          <WrappedComponent {...props} />
        </PermissionGuard>
      );
    };

    ProtectedComponent.displayName = `withPermission(${WrappedComponent.displayName || WrappedComponent.name})`;

    return ProtectedComponent;
  };

// ----------------------------------------------------------------------

/**
 * Componente específico para proteger botones
 * Aplica estilos disabled si no tiene permisos en lugar de ocultar
 */
export const PermissionButton = ({
  children,
  permission,
  permissions,
  requireAll = false,
  hideWhenDenied = false,
  ...buttonProps
}) => {
  const hasPermission = useHasPermission(permission);
  const hasAnyPermissions = useHasAnyPermission(permissions);
  const hasAllPermissions = useHasAllPermissions(permissions);

  // Determinar si tiene permisos
  let hasRequiredPermissions = false;

  if (permission) {
    hasRequiredPermissions = hasPermission;
  } else if (permissions && Array.isArray(permissions)) {
    hasRequiredPermissions = requireAll ? hasAllPermissions : hasAnyPermissions;
  }

  // Si debe ocultarse cuando se deniega y no tiene permisos
  if (hideWhenDenied && !hasRequiredPermissions) {
    return null;
  }

  // Aplicar disabled si no tiene permisos
  const finalProps = {
    ...buttonProps,
    disabled: !hasRequiredPermissions || buttonProps.disabled,
  };

  return <>{children(finalProps)}</>;
};

PermissionButton.propTypes = {
  children: PropTypes.func.isRequired,
  permission: PropTypes.string,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
  hideWhenDenied: PropTypes.bool,
};

// ----------------------------------------------------------------------

/**
 * Componente para mostrar diferentes contenidos basado en permisos
 * Útil para mostrar diferentes versiones de un componente según permisos
 */
export const PermissionSwitch = ({ cases, defaultCase = null }) => {
  const {
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
  } = usePermissions();

  // Encontrar el primer caso que cumpla con los permisos
  const validCase = cases.find(({ permission, permissions, requireAll = false }) => {
    if (permission) {
      return checkPermission(permission);
    }

    if (permissions && Array.isArray(permissions)) {
      return requireAll ? checkAllPermissions(permissions) : checkAnyPermission(permissions);
    }

    return false;
  });

  if (validCase) {
    return <>{validCase.component}</>;
  }

  return defaultCase ? <>{defaultCase}</> : null;
};

PermissionSwitch.propTypes = {
  cases: PropTypes.arrayOf(
    PropTypes.shape({
      permission: PropTypes.string,
      permissions: PropTypes.arrayOf(PropTypes.string),
      requireAll: PropTypes.bool,
      component: PropTypes.node.isRequired,
    })
  ).isRequired,
  defaultCase: PropTypes.node,
};
